import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { displayToastSuccess } from '@/helpers/display-toast';
import { useApiError } from '@/modules/common/hooks/use-api-error';
import { isFeatureEnabled } from '@/modules/common/hooks/use-feature-flags';
import {
  invalidateSuggestedApproversQueries,
  invalidateTeamMemberRatesOnProjectQueries,
  invalidateTimeTrackerProjectsQueries,
  invalidateTimeTrackingQueries,
} from '@/modules/common/utils/query-invalidation';

import { duplicateAssignment } from '../../api/assignments.api';
import {
  addOptimisticAssignmentToCache,
  buildOptimisticAssignment,
  buildOptimisticPlannerAssignment,
  cancelAssignmentQueries,
  findPlannerAssignmentInCache,
  invalidateAllAssignmentQueries,
  OptimisticContext,
  patchCacheAfterMutation,
  refetchActiveAssignmentQueriesInBackground,
  rollbackAssignmentData,
  snapshotAssignmentData,
} from '../../helpers/assignment-cache-utils';
import { AssignmentResponse, DuplicateAssignmentWithUIRequest } from '../../types/dto';

/**
 * Hook for duplicating an existing assignment via POST /api/assignments/duplicate.
 *
 * Sends only `assignmentId`, `startDate`, and `endDate` to the backend; all other
 * fields are inherited from the source assignment. Uses the same optimistic-update
 * and cache-patching strategy as `useCreateAssignment`.
 *
 * @param options - Optional TanStack Query mutation options
 * @returns Mutation object with mutate, isPending, etc.
 *
 * @example
 * ```typescript
 * const duplicateMutation = useDuplicateAssignment();
 *
 * duplicateMutation.mutate({
 *   payload: { assignmentId: 42, startDate: '2025-02-01', endDate: '2025-02-28' },
 *   ui: { userId: 1, projectId: 2, projectName: 'My Project', ... },
 * });
 * ```
 */
export function useDuplicateAssignment(
  options?: UseMutationOptions<AssignmentResponse, Error, DuplicateAssignmentWithUIRequest>
) {
  const queryClient = useQueryClient();
  const { t } = useTranslation('authenticated.planner');
  const { displayToastErrorFromApiWithFallback } = useApiError();

  const { onSuccess, onError, ...restOptions } = options ?? {};

  return useMutation({
    ...restOptions,
    mutationFn: (request: DuplicateAssignmentWithUIRequest) => duplicateAssignment(request.payload),

    onMutate: async (variables: DuplicateAssignmentWithUIRequest): Promise<OptimisticContext> => {
      const { payload, ui } = variables;

      // Cancel outgoing refetches to prevent overwriting the optimistic update
      await cancelAssignmentQueries(queryClient);

      // Snapshot all assignment queries for rollback on error
      const previousData = snapshotAssignmentData(queryClient);

      // Skip optimistic update when UI data is absent — cannot build a
      // realistic placeholder without knowing userId / project details.
      if (!ui) {
        return { ...previousData, tempAssignmentId: undefined };
      }

      const optimisticAssignment = buildOptimisticAssignment({
        startDate: payload.startDate,
        endDate: payload.endDate,
        status: ui.status,
        doSkipWeekdays: ui.doSkipWeekdays,
        isBillable: ui.isBillable,
        label: ui.label,
        minutesPerDay: ui.minutesPerDay,
        note: ui.note,
        isBackgroundWork: ui.isBackgroundWork,
        probability: ui.probability,
        rateSource: ui.rateSource,
        customRate: ui.customRate,
        projectId: ui.projectId,
        projectName: ui.projectName,
        projectColorHex: ui.projectColorHex,
      });

      // The duplicate keeps the source's project, so its cached project node applies as is
      const sourcePlannerProject = findPlannerAssignmentInCache(queryClient, payload.assignmentId)
        ?.plannerAssignment.project;
      const optimisticPlannerAssignment = buildOptimisticPlannerAssignment(
        optimisticAssignment,
        sourcePlannerProject
      );
      addOptimisticAssignmentToCache(queryClient, ui.userId, optimisticPlannerAssignment);

      return { ...previousData, tempAssignmentId: optimisticAssignment.id };
    },

    onSuccess: (data, variables, context) => {
      if (isFeatureEnabled('usePlannerCachePatching')) {
        const ctx = context as OptimisticContext;

        // Replace temp ID with the real server ID and recalculate utilization
        patchCacheAfterMutation(queryClient, {
          assignmentId: ctx.tempAssignmentId,
          serverData: data,
          userId: variables.ui?.userId,
          newDates: {
            startDate: variables.payload.startDate,
            endDate: variables.payload.endDate,
          },
          customRateCurrencyCode: variables.ui?.customRateCurrencyCode,
        });

        // Fire-and-forget background refetch to reconcile activePeriods from backend
        // (client-side cannot compute rate-change-based period splits).
        refetchActiveAssignmentQueriesInBackground(queryClient);
      } else {
        invalidateAllAssignmentQueries(queryClient);
      }

      // Duplicate affects rate calculations in the same way as create
      invalidateTeamMemberRatesOnProjectQueries(queryClient);
      // Assignment changes affect booked hours shown on the time tracking page
      invalidateTimeTrackingQueries(queryClient);
      // Assignment changes can alter the time-off suggested approvers.
      invalidateSuggestedApproversQueries(queryClient);
      // Assignment changes can flip which projects count as "booked" in the
      // Time Tracking modal's project picker.
      invalidateTimeTrackerProjectsQueries(queryClient);

      displayToastSuccess(
        t('toasts.assignmentDuplicatedSuccess', {
          userName: variables.ui?.userName ?? '',
          projectName: variables.ui?.projectName ?? '',
        })
      );

      onSuccess?.(data, variables, context);
    },

    onError: (error, variables, context) => {
      // Restore pre-mutation cache state
      rollbackAssignmentData(queryClient, context as OptimisticContext);

      displayToastErrorFromApiWithFallback(
        error,
        'authenticated.planner:toasts.duplicateAssignmentError',
        {
          interpolation: {
            userName: variables.ui?.userName ?? '',
            projectName: variables.ui?.projectName ?? '',
          },
        }
      );

      onError?.(error, variables, context);
    },
  });
}
